import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientAPI, emailTemplateAPI } from '../../utils/crmApi';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useDialog, Dialog, DialogHeader, DialogBody, DialogFooter } from '../../components/kibo-ui/dialog';
import {
    EnvelopeIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon,
    EyeIcon,
    DocumentDuplicateIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    TagIcon,
    CodeBracketIcon,
    DocumentTextIcon,
    SparklesIcon,
    ClipboardDocumentListIcon,
    GlobeAltIcon,
    AdjustmentsHorizontalIcon,
    XCircleIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Built-in Rich Text Editor Component
const RichTextEditor = ({ value, onChange, height = 300, placeholder = "Enter your content..." }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showVariables, setShowVariables] = useState(false);
    
    const availableVariables = [
        { name: 'client.name', description: 'Client full name', example: 'John Doe' },
        { name: 'client.email', description: 'Client email address', example: 'john@example.com' },
        { name: 'client.company', description: 'Client company name', example: 'Acme Corp' },
        { name: 'account.name', description: 'Account company name', example: 'Tech Solutions Inc' },
        { name: 'account.contact', description: 'Account contact person', example: 'Jane Smith' },
        { name: 'partner.name', description: 'Assigned partner name', example: 'Maria Garcia' },
        { name: 'current.date', description: 'Current date', example: '2024-01-15' },
        { name: 'current.time', description: 'Current time', example: '14:30' },
        { name: 'task.title', description: 'Task title', example: 'NIE Application' },
        { name: 'task.dueDate', description: 'Task due date', example: '2024-02-01' },
        { name: 'agency.name', description: 'Agency name', example: 'LiveLoveSpain' },
        { name: 'agency.motto', description: 'Agency motto', example: 'Your Spanish Journey Starts Here' }
    ];

    const insertVariable = (variableName) => {
        const variableText = `{{${variableName}}}`;
        const textarea = document.getElementById('rich-text-editor');
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newValue = value.substring(0, start) + variableText + value.substring(end);
        onChange(newValue);
        
        // Set cursor position after the inserted variable
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + variableText.length, start + variableText.length);
        }, 0);
    };

    const formatText = (command) => {
        const textarea = document.getElementById('rich-text-editor');
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = value.substring(start, end);
        
        let formattedText = selectedText;
        switch (command) {
            case 'bold':
                formattedText = `<strong>${selectedText}</strong>`;
                break;
            case 'italic':
                formattedText = `<em>${selectedText}</em>`;
                break;
            case 'underline':
                formattedText = `<u>${selectedText}</u>`;
                break;
            case 'link':
                formattedText = `<a href="#">${selectedText}</a>`;
                break;
            case 'paragraph':
                formattedText = `<p>${selectedText}</p>`;
                break;
            case 'list':
                formattedText = `<ul><li>${selectedText}</li></ul>`;
                break;
        }
        
        const newValue = value.substring(0, start) + formattedText + value.substring(end);
        onChange(newValue);
    };

    return (
        <div className={`border rounded-lg transition-colors ${isFocused ? 'border-blue-500 ring-1 ring-blue-500' : 'border-input'}`}>
            {/* Toolbar */}
            <div className="flex items-center justify-between p-2 bg-muted border-b border-border overflow-x-auto scrollbar-thin">
                <div className="flex items-center space-x-1 min-w-0 flex-1">
                    <button
                        type="button"
                        onClick={() => formatText('bold')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted font-bold flex-shrink-0"
                        title="Bold"
                    >
                        B
                    </button>
                    <button
                        type="button"
                        onClick={() => formatText('italic')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted italic flex-shrink-0"
                        title="Italic"
                    >
                        I
                    </button>
                    <button
                        type="button"
                        onClick={() => formatText('underline')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted underline flex-shrink-0"
                        title="Underline"
                    >
                        U
                    </button>
                    <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1 flex-shrink-0"></div>
                    <button
                        type="button"
                        onClick={() => formatText('link')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted flex-shrink-0"
                        title="Link"
                    >
                        🔗
                    </button>
                    <button
                        type="button"
                        onClick={() => formatText('paragraph')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted flex-shrink-0"
                        title="Paragraph"
                    >
                        ¶
                    </button>
                    <button
                        type="button"
                        onClick={() => formatText('list')}
                        className="px-2 py-1 text-xs bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted flex-shrink-0"
                        title="List"
                    >
                        • List
                    </button>
                    <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1 flex-shrink-0"></div>
                    <button
                        type="button"
                        onClick={() => setShowVariables(!showVariables)}
                        className={`px-2 py-1 text-xs border rounded flex items-center space-x-1 flex-shrink-0 ${
                            showVariables ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300' : 'bg-card border-input hover:bg-muted'
                        }`}
                        title="Insert Variables"
                    >
                        <TagIcon className="h-3 w-3" />
                        <span className="hidden sm:inline">Variables</span>
                    </button>
                </div>
                <div className="flex items-center space-x-2 text-xs text-muted-foreground flex-shrink-0 ml-2">
                    <span>{value?.length || 0} chars</span>
                    <span>•</span>
                    <span>{value?.split('\n').length || 1} lines</span>
                </div>
            </div>

            {/* Variable Panel */}
            {showVariables && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Click to insert variables:</span>
                        <button
                            type="button"
                            onClick={() => setShowVariables(false)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        >
                            <XCircleIcon className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {availableVariables.map((variable, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => insertVariable(variable.name)}
                                className="px-2 py-1 bg-card border border-blue-300 dark:border-blue-700 rounded text-xs hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:border-blue-400 dark:hover:border-blue-600 transition-colors text-left"
                                title={variable.description}
                            >
                                <div className="font-mono text-blue-600 dark:text-blue-400">{variable.name}</div>
                                <div className="text-muted-foreground text-xs truncate">{variable.description}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Text Editor */}
            <textarea
                id="rich-text-editor"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={placeholder}
                className="w-full p-3 resize-none focus:outline-none font-mono text-sm bg-background text-foreground placeholder:text-muted-foreground"
                style={{ height: `${height}px` }}
            />
        </div>
    );
};

// Variable Management Component
const VariableManager = ({ variables, onVariablesChange }) => {
    const [newVariable, setNewVariable] = useState({ name: '', description: '' });
    const [showAddForm, setShowAddForm] = useState(false);

    const addVariable = () => {
        if (newVariable.name && newVariable.description) {
            onVariablesChange([...variables, newVariable]);
            setNewVariable({ name: '', description: '' });
            setShowAddForm(false);
        }
    };

    const removeVariable = (index) => {
        onVariablesChange(variables.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-foreground">Template Variables</h4>
                <button
                    type="button"
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                >
                    <PlusIcon className="h-4 w-4" />
                    <span>Add Variable</span>
                </button>
            </div>

            {/* Add Variable Form */}
            {showAddForm && (
                <div className="p-3 bg-muted rounded-lg border border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-foreground mb-1">
                                Variable Name
                            </label>
                            <input
                                type="text"
                                value={newVariable.name}
                                onChange={(e) => setNewVariable(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., client.name"
                                className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:outline-none focus:ring-1 f focus:ring-2 focus:ring-ring focus:border-ringocus:ring-ring"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-foreground mb-1">
                                Description
                            </label>
                            <input
                                type="text"
                                value={newVariable.description}
                                onChange={(e) => setNewVariable(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="e.g., Client's full name"
                                className="w-full px-2 py-1 text-sm border border-input bg-background text-foreground rounded focus:outline-none focus:ring-1 f focus:ring-2 focus:ring-ring focus:border-ringocus:ring-ring"
                            />
                        </div>
                    </div>
                    <div className="flex items-center justify-end space-x-2 mt-3">
                        <button
                            type="button"
                            onClick={() => setShowAddForm(false)}
                            className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={addVariable}
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Add
                        </button>
                    </div>
                </div>
            )}

            {/* Variables List */}
            <div className="space-y-2">
                {variables.map((variable, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-card border border-border rounded">
                        <div className="flex-1">
                            <div className="font-mono text-sm text-blue-600">{variable.name}</div>
                            <div className="text-xs text-muted-foreground">{variable.description}</div>
                        </div>
                        <button
                            type="button"
                            onClick={() => removeVariable(index)}
                            className="text-red-600 hover:text-red-800"
                        >
                            <TrashIcon className="h-4 w-4" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Preview Component
const EmailPreview = ({ template, previewData = {} }) => {
    const [activeTab, setActiveTab] = useState('html');
    
    const replaceVariables = (content) => {
        if (!content) return '';
        return content.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
            return previewData[variable] || `{{${variable}}}`;
        });
    };

    const htmlContent = replaceVariables(template.htmlBody);
    const textContent = replaceVariables(template.textBody);

    return (
        <div className="space-y-4">
            <div className="flex border-b border-border">
                <button
                    type="button"
                    onClick={() => setActiveTab('html')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        activeTab === 'html' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    HTML Preview
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab('text')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        activeTab === 'text' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                    Text Preview
                </button>
            </div>

            <div className="border border-border rounded-lg">
                <div className="p-3 bg-muted border-b border-border">
                    <div className="text-sm font-medium text-foreground">Subject: {replaceVariables(template.subject)}</div>
                </div>
                <div className="p-4">
                    {activeTab === 'html' ? (
                        <div
                            className="prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: htmlContent }}
                        />
                    ) : (
                        <pre className="whitespace-pre-wrap text-sm text-foreground">{textContent}</pre>
                    )}
                </div>
            </div>
        </div>
    );
};

// AI Template Generator Dialog Component (now used as a standalone modal)
const AITemplateGeneratorDialog = ({ isOpen, onClose, onGenerate, generating }) => {
    const [prompt, setPrompt] = useState('');
    const [description, setDescription] = useState('');
    const [tone, setTone] = useState('professional');
    const [category, setCategory] = useState('general');

    const toneOptions = [
        { value: 'professional', label: 'Professional' },
        { value: 'friendly', label: 'Friendly' },
        { value: 'formal', label: 'Formal' },
        { value: 'casual', label: 'Casual' },
        { value: 'urgent', label: 'Urgent' }
    ];

    const categoryOptions = [
        { value: 'general', label: 'General' },
        { value: 'welcome', label: 'Welcome' },
        { value: 'follow_up', label: 'Follow-up' },
        { value: 'reminder', label: 'Reminder' },
        { value: 'notification', label: 'Notification' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'support', label: 'Support' }
    ];

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        if (!prompt.trim()) {
            toast.error('Please enter a description for the email template');
            return;
        }
        onGenerate(prompt, description, tone, category);
    };

    if (!isOpen) return null;

    return (
        <Dialog isOpen={isOpen} onClose={onClose} size="lg">
            <DialogHeader title="AI Email Template Generator" onClose={onClose} />
            <DialogBody>
                <p className="text-sm text-muted-foreground mb-4">
                    Describe the email template you want to create. The AI will generate professional HTML email content for you.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Template Purpose / Context *
                        </label>
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="e.g., A welcome email for new clients moving to Spain..."
                            rows={3}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            required
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Briefly describe what this email template is for
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Email Content / Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="e.g., Welcome new clients, explain the relocation process, provide next steps, include contact information..."
                            rows={5}
                            className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            Describe the detailed content you want in the email body. Be specific about what information to include.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Tone
                            </label>
                            <select
                                value={tone}
                                onChange={(e) => setTone(e.target.value)}
                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            >
                                {toneOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Category
                            </label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                            >
                                {categoryOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </form>
            </DialogBody>
            <DialogFooter>
                <button
                    type="button"
                    onClick={onClose}
                    disabled={generating}
                    className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80 disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={generating || !prompt.trim()}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    style={{ backgroundColor: '#9333ea' }}
                >
                    {generating ? (
                        <>
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                            Generating...
                        </>
                    ) : (
                        <>
                            <SparklesIcon className="h-4 w-4" />
                            Generate Template
                        </>
                    )}
                </button>
            </DialogFooter>
        </Dialog>
    );
};

const EmailTemplatesEnhanced = () => {
    const { hasFeature } = useSubscription();
    const { openDialog, closeDialog } = useDialog();
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTag, setFilterTag] = useState('');

    const [previewData, setPreviewData] = useState({
        'client.name': 'John Doe',
        'client.email': 'john@example.com',
        'client.company': 'Acme Corp',
        'account.name': 'Tech Solutions Inc',
        'partner.name': 'Maria Garcia',
        'current.date': new Date().toLocaleDateString(),
        'current.time': new Date().toLocaleTimeString(),
        'task.title': 'NIE Application',
        'task.dueDate': '2024-02-01',
        'agency.name': 'LiveLoveSpain',
        'agency.motto': 'Your Spanish Journey Starts Here'
    });

    const categories = [
        { value: 'general', label: 'General', color: 'bg-muted text-foreground' },
        { value: 'welcome', label: 'Welcome', color: 'bg-green-100 text-green-800' },
        { value: 'follow_up', label: 'Follow-up', color: 'bg-blue-100 text-blue-800' },
        { value: 'reminder', label: 'Reminder', color: 'bg-yellow-100 text-yellow-800' },
        { value: 'notification', label: 'Notification', color: 'bg-purple-100 text-purple-800' },
        { value: 'marketing', label: 'Marketing', color: 'bg-pink-100 text-pink-800' },
        { value: 'support', label: 'Support', color: 'bg-red-100 text-red-800' }
    ];

    // Skeleton loading states
    const [skeletonData, setSkeletonData] = useState({
        templates: true,
        modal: false
    });

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            setSkeletonData(prev => ({ ...prev, templates: true }));
            
            try {
                const response = await emailTemplateAPI.getAll();
                if (response.data && response.data.data) {
                    // Map backend field names to frontend
                    const mappedTemplates = response.data.data.map(t => ({
                        ...t,
                        htmlBody: t.bodyHtml || t.htmlBody || '',
                        textBody: t.bodyText || t.textBody || '',
                        code: t.code || t.templateId || '', // Ensure code is mapped
                        variableDefaults: t.variableDefaults || {},
                        omitEmptyVariables: t.omitEmptyVariables || false
                    }));
                    setTemplates(mappedTemplates);
                    setSkeletonData(prev => ({ ...prev, templates: false }));
                    return;
                }
            } catch (apiError) {
                console.warn('API call failed, using mock data:', apiError);
            }
            
            // Fallback to mock data
            const mockTemplates = [
                {
                    _id: '1',
                    name: 'Welcome Email',
                    subject: 'Welcome to {{agency.name}}!',
                    htmlBody: '<p>Hello {{client.name}},</p><p>Welcome to {{agency.name}}! We\'re excited to help you with your Spanish journey.</p>',
                    textBody: 'Hello {{client.name}},\n\nWelcome to {{agency.name}}! We\'re excited to help you with your Spanish journey.',
                    category: 'welcome',
                    tags: ['onboarding', 'new client'],
                    variables: [
                        { name: 'client.name', description: 'Client Name' },
                        { name: 'agency.name', description: 'Agency Name' }
                    ],
                    isActive: true,
                    description: 'Initial welcome email for new clients',
                    code: 'welcome_email'
                },
                {
                    _id: '2',
                    name: 'Task Reminder',
                    subject: 'Reminder: {{task.title}} is due soon',
                    htmlBody: '<p>Hi {{client.name}},</p><p>Just a reminder that your task <strong>{{task.title}}</strong> is due on {{task.dueDate}}.</p>',
                    textBody: 'Hi {{client.name}},\n\nJust a reminder that your task {{task.title}} is due on {{task.dueDate}}.',
                    category: 'reminder',
                    tags: ['task', 'reminder'],
                    variables: [
                        { name: 'client.name', description: 'Client Name' },
                        { name: 'task.title', description: 'Task Title' },
                        { name: 'task.dueDate', description: 'Task Due Date' }
                    ],
                    isActive: true,
                    description: 'Reminder for upcoming tasks',
                    code: 'task_reminder'
                }
            ];
            
            setTemplates(mockTemplates);
            setSkeletonData(prev => ({ ...prev, templates: false }));
        } catch (error) {
            console.error('Failed to fetch templates:', error);
            toast.error('Failed to load email templates');
        } finally {
            setLoading(false);
        }
    };


    const handleDelete = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this email template?')) return;
        
        try {
            // Mock delete
            toast.success('Template deleted successfully');
            fetchTemplates();
        } catch (error) {
            console.error('Failed to delete template:', error);
            toast.error('Failed to delete template');
        }
    };

    // Extract variables from content
    const extractVariables = (content) => {
        if (!content) return [];
        const variableRegex = /\{\{([^}]+)\}\}/g;
        const variables = new Set();
        let match;
        while ((match = variableRegex.exec(content)) !== null) {
            variables.add(match[1].trim());
        }
        return Array.from(variables);
    };

    const handleEdit = (template) => {
        navigate(`/email-templates/${template._id}/edit`);
    };

    const handlePreview = (template) => {
        setPreviewTemplate(template);
    };

    const handleDuplicate = async (template) => {
        try {
            const response = await emailTemplateAPI.duplicate(template._id);
            if (response.data && response.data.data) {
                toast.success('Template duplicated successfully!');
                fetchTemplates();
                // Navigate to edit the duplicated template
                navigate(`/email-templates/${response.data.data._id}/edit`);
            }
        } catch (error) {
            console.error('Failed to duplicate template:', error);
            toast.error('Failed to duplicate template');
        }
    };


    const getUsedVariables = (content) => {
        if (!content) return [];
        const variableRegex = /\{\{([^}]+)\}\}/g;
        const matches = content.match(variableRegex);
        if (!matches) return [];
        return [...new Set(matches.map(match => match.replace(/[{}]/g, '')))];
    };

    const filteredTemplates = templates.filter(template => {
        const matchesSearch = 
            (template.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (template.subject || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTag = !filterTag || (template.tags || []).includes(filterTag);
        return matchesSearch && matchesTag;
    });

    const allTags = [...new Set((templates || []).flatMap(t => t.tags || []))];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Email Templates</h1>
                        <p className="text-muted-foreground mt-1">Create and manage email templates with rich text editing and variables</p>
                    </div>
                    <button
                        onClick={() => navigate('/email-templates/new')}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <PlusIcon className="h-5 w-5" />
                        New Template
                    </button>
                </div>
            </div>

            {/* Filters and Search */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        />
                    </div>
                    <select
                        value={filterTag}
                        onChange={(e) => setFilterTag(e.target.value)}
                        className="px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                    >
                        <option value="">All Tags</option>
                        {allTags.map(tag => (
                            <option key={tag} value={tag}>{tag}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setFilterTag('');
                        }}
                        className="px-4 py-2 text-muted-foreground border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-lg hover:bg-muted transition-colors"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {skeletonData.templates ? (
                    // Skeleton loading
                    Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="bg-card rounded-lg shadow-sm border border-border p-6 animate-pulse">
                            <div className="space-y-3">
                                <div className="h-4 bg-muted rounded w-3/4"></div>
                                <div className="h-3 bg-muted rounded w-1/2"></div>
                                <div className="h-3 bg-muted rounded w-full"></div>
                                <div className="h-3 bg-muted rounded w-2/3"></div>
                            </div>
                        </div>
                    ))
                ) : (
                    filteredTemplates.map((template) => {
                        const categoryInfo = categories.find(cat => cat.value === template.category);
                        return (
                            <div key={template._id} className="bg-card rounded-lg shadow-sm border border-border p-6 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-foreground mb-1">{template.name}</h3>
                                        <p className="text-sm text-muted-foreground mb-2">{template.subject}</p>
                                        {template.description && (
                                            <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => handlePreview(template)}
                                            className="p-2 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Preview"
                                        >
                                            <EyeIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleEdit(template)}
                                            className="p-2 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Edit"
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDuplicate(template)}
                                            className="p-2 text-muted-foreground hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                            title="Duplicate"
                                        >
                                            <DocumentDuplicateIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(template._id)}
                                            className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Delete"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryInfo?.color || 'bg-muted text-foreground'}`}>
                                            {categoryInfo?.label || template.category}
                                        </span>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${template.isActive ? 'bg-green-100 text-green-800' : 'bg-muted text-foreground'}`}>
                                            {template.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                    
                                    {template.tags && template.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {template.tags.map((tag, index) => (
                                                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {template.variables && template.variables.length > 0 && (
                                        <div className="text-xs text-muted-foreground">
                                            <span className="font-medium">Variables:</span> {template.variables.length}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Preview Modal */}
            {previewTemplate && (
                <div className="fixed inset-0 bg-black/50 dark:bg-black/70 overflow-y-auto h-full w-full z-[9999]">
                    <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-lg bg-card">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-semibold text-foreground">Email Preview</h3>
                            <button
                                onClick={() => setPreviewTemplate(null)}
                                className="text-muted-foreground hover:text-muted-foreground"
                            >
                                <XCircleIcon className="h-6 w-6" />
                            </button>
                        </div>
                        <EmailPreview template={previewTemplate} previewData={previewData} />
                    </div>
                </div>
            )}

        </div>
    );
};

export default EmailTemplatesEnhanced;
